import React from "react";
import PropTypes from "prop-types";

/**
 * Semicircular risk gauge (180°) with 6 colored sectors and a needle.
 * Pure SVG (no external deps beyond React).
 */
const SECTORS = [
  { threshold: 0.10, color: "#10B981", label: "Very Low" },
  { threshold: 0.25, color: "#84CC16", label: "Low" },
  { threshold: 0.50, color: "#FBBF24", label: "Intermediate" },
  { threshold: 0.75, color: "#F97316", label: "Intermediate-High" },
  { threshold: 0.90, color: "#EF4444", label: "High" },
  { threshold: 1.01, color: "#7F1D1D", label: "Very High" },
];

const polar = (cx, cy, r, angleDeg) => {
  const a = (angleDeg - 180) * (Math.PI / 180);
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

const arcPath = (cx, cy, r, startAngle, endAngle, thickness) => {
  const outerStart = polar(cx, cy, r, startAngle);
  const outerEnd = polar(cx, cy, r, endAngle);
  const innerStart = polar(cx, cy, r - thickness, endAngle);
  const innerEnd = polar(cx, cy, r - thickness, startAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${r} ${r} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${r - thickness} ${r - thickness} 0 ${large} 0 ${innerEnd.x} ${innerEnd.y}`,
    "Z",
  ].join(" ");
};

export default function RiskGauge({ probability = 0, riskLabel = "" }) {
  const cx = 220, cy = 200, r = 170, thickness = 42;
  const p = Math.max(0, Math.min(1, probability));
  const needleAngle = p * 180; // 0..180
  const needle = polar(cx, cy, r - 6, needleAngle);
  const needleBack = polar(cx, cy, 14, needleAngle + 180);

  // Build sectors by threshold.
  const sectors = [];
  let prev = 0;
  for (const s of SECTORS) {
    const a1 = prev * 180;
    const a2 = Math.min(s.threshold, 1) * 180;
    if (a2 > a1) sectors.push({ a1, a2, color: s.color, label: s.label });
    prev = s.threshold;
    if (prev >= 1) break;
  }

  return (
    <div className="w-full flex flex-col items-center" data-testid="risk-gauge">
      <svg viewBox="0 0 440 230" className="w-full max-w-[440px]">
        {sectors.map((s, i) => (
          <path
            key={i}
            d={arcPath(cx, cy, r, s.a1, s.a2, thickness)}
            fill={s.color}
            opacity={0.92}
          />
        ))}
        {/* Tick marks at threshold positions */}
        {[0, 0.1, 0.25, 0.5, 0.75, 0.9, 1.0].map((t, i) => {
          const a = t * 180;
          const p1 = polar(cx, cy, r + 4, a);
          const p2 = polar(cx, cy, r - thickness - 4, a);
          return (
            <line
              key={i}
              x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
              stroke="#0A0F17"
              strokeWidth={1}
              opacity={0.4}
            />
          );
        })}
        {/* Threshold labels */}
        {[0, 0.25, 0.5, 0.75, 1.0].map((t, i) => {
          const a = t * 180;
          const pos = polar(cx, cy, r + 18, a);
          return (
            <text
              key={i}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              fontSize="11"
              fontFamily="IBM Plex Mono, monospace"
              fill="#475569"
            >
              {t.toFixed(2)}
            </text>
          );
        })}
        {/* Needle */}
        <line
          x1={needleBack.x}
          y1={needleBack.y}
          x2={needle.x}
          y2={needle.y}
          stroke="#0A0F17"
          strokeWidth={3}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={8} fill="#0A0F17" />
        <circle cx={cx} cy={cy} r={3} fill="#FFFFFF" />
      </svg>
      <div className="text-center -mt-2">
        <div
          className="text-5xl font-mono tabular-nums font-semibold text-slate-900"
          data-testid="gauge-probability"
        >
          {p.toFixed(4)}
        </div>
        <div
          className="mt-1 data-label"
          data-testid="gauge-risk-class"
        >
          {riskLabel || "—"}
        </div>
      </div>
    </div>
  );
}

RiskGauge.propTypes = {
  probability: PropTypes.number,
  riskLabel: PropTypes.string,
};
